﻿<script setup lang="ts">
/**
 * OnboardModal · 新手引导弹层（新接入组织一次性三步引导）
 * ------------------------------------------------------------
 * 原型对应：EMA_FRESH.scene 的接入引导（原型 L6900-6935）
 * 三步：核对授权范围 → 浏览架构资产 → 接入业务系统。
 *
 * 用法（挂在工作台顶层即可，自判断显隐）：
 *   <OnboardModal />
 * 显隐条件：scope.isFresh && localStorage('ema.onboarded') 为空；
 * 关闭时写入 ema.onboarded=1，只弹一次。
 */
import { ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useScopeStore } from '@/domain/scope/store'

const LS_KEY = 'ema.onboarded'
const router = useRouter()
const scope = useScopeStore()

const visible = ref(false)
const step = ref(0)

const steps = [
  {
    ic: '权', title: '核对授权范围',
    body: '你的组织与角色已就绪。顶栏组织菜单与「我的」页可以看到：授权应用域、可用应用数、执行权限口径。所有动作只会在授权范围内执行。',
    hint: '切组织后页面、权限、数据全部跟随'
  },
  {
    ic: '构', title: '浏览架构资产',
    body: '应用、业务、数据三份架构清单现在就能浏览：应用架构全量（L1→L5）、业务节点契约、业务对象清单。先「看得见」，再谈接入。',
    hint: '「应用」页顶部有四份架构条'
  },
  {
    ic: '接', title: '接入业务系统',
    body: '异常处置、任务、日程、讨论群等运行数据视图，将在业务系统接入后自动出现。接入前它们显示为空状态，并给你浏览架构的入口。',
    hint: '当前可用：架构浏览 + 授权核对 + 全局搜索'
  }
]

/* 组织是异步加载的（main.ts 预热不 await），挂载时 current 可能为 null ——
   必须 watch，isFresh 变真（首载完成或切到 fresh 组织）且未看过引导时弹出 */
watch(
  () => [scope.isFresh, scope.current?.id] as const,
  ([fresh]) => {
    if (fresh && !localStorage.getItem(LS_KEY)) visible.value = true
  },
  { immediate: true }
)

function next() { if (step.value < steps.length - 1) step.value++ }
function prev() { if (step.value > 0) step.value-- }
function finish() {
  localStorage.setItem(LS_KEY, '1')
  visible.value = false
}
function finishToArch() {
  finish()
  router.push('/arch')
}
</script>

<template>
  <Teleport to="body">
    <div v-if="visible" class="ob-mask">
      <div class="ob-modal">
        <div class="ob-head">
          <span class="ob-brand">企</span>
          <div>
            <h3>欢迎接入 · {{ scope.current?.short ?? '新组织' }} 已就绪</h3>
            <p>三步开始使用 EMA 企管助手</p>
          </div>
        </div>

        <div class="ob-dots">
          <i v-for="i in steps.length" :key="i" :class="{ on: i - 1 === step }" />
        </div>

        <div class="ob-step">
          <div class="ob-ic">{{ steps[step].ic }}</div>
          <h4>{{ step + 1 }}. {{ steps[step].title }}</h4>
          <p class="ob-body">{{ steps[step].body }}</p>
          <p class="ob-hint">{{ steps[step].hint }}</p>
        </div>

        <div class="ob-acts">
          <button v-if="step > 0" class="ubtn ghost" @click="prev">上一步</button>
          <span class="sp" />
          <button class="ubtn secondary" @click="finishToArch">先看架构总览</button>
          <button v-if="step < steps.length - 1" class="ubtn primary" @click="next">下一步</button>
          <button v-else class="ubtn primary" @click="finish">开始体验</button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.ob-mask {
  position: fixed; inset: 0; z-index: 3000;
  background: color-mix(in srgb, var(--ink) 44%, transparent);
  display: flex; align-items: center; justify-content: center;
  padding: var(--sp-8);
}
.ob-modal {
  width: 460px; max-width: 100%;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-l);
  box-shadow: var(--sh-3);
  padding: var(--sp-12);
}
.ob-head { display: flex; gap: var(--sp-5); align-items: center; margin-bottom: var(--sp-6); }
.ob-brand {
  width: 44px; height: 44px; flex: none;
  border-radius: var(--r-m);
  background: var(--accent-solid); color: #fff;
  font-size: var(--fs-lg); font-weight: var(--fw-bold);
  display: flex; align-items: center; justify-content: center;
}
.ob-head h3 { font-size: var(--fs-md); font-weight: var(--fw-semibold); color: var(--ink); }
.ob-head p { font-size: var(--fs-xs); color: var(--ink-4); margin-top: var(--sp-1); }
.ob-dots { display: flex; gap: var(--sp-2); margin-bottom: var(--sp-6); }
.ob-dots i { height: 4px; flex: 1; border-radius: 2px; background: var(--line); }
.ob-dots i.on { background: var(--accent-solid); }
.ob-step { min-height: 148px; }
.ob-ic {
  width: 40px; height: 40px; margin-bottom: var(--sp-4);
  border-radius: var(--r-s);
  background: var(--accent-soft); color: var(--accent);
  font-size: var(--fs-lg); font-weight: var(--fw-bold);
  display: flex; align-items: center; justify-content: center;
}
.ob-step h4 { font-size: var(--fs-base); font-weight: var(--fw-semibold); color: var(--ink); margin-bottom: var(--sp-3); }
.ob-body { font-size: var(--fs-sm); color: var(--ink-2); line-height: 1.8; }
.ob-hint {
  margin-top: var(--sp-4); font-size: var(--fs-xs); color: var(--ink-4);
  background: var(--surface-3); border-radius: var(--r-s); padding: var(--sp-3) var(--sp-4);
}
.ob-acts { display: flex; gap: var(--sp-4); margin-top: var(--sp-8); align-items: center; }
.ob-acts .sp { flex: 1; }
</style>
